/**
 * Pure guard enforcing the settled product rule for `groupMembers` writes:
 * - Sections (`groupType === 'parent'`) are conceptual; nobody joins them
 *   directly — refuse any membership write targeting one.
 * - A user may belong to at most ONE subgroup per organization. Groups with
 *   no `organization` are unconstrained (the rule is per-org).
 * - A stale/artifact direct membership in a section does not count as "the
 *   other subgroup" — only subgroup memberships can conflict.
 *
 * No Firestore imports here, so this stays trivially unit-testable. The
 * Firestore-touching wrapper (`assertMembershipAllowed` in
 * `group-members.ts`) fetches the docs and feeds this module raw data.
 *
 * Mirrors mobile/src/lib/firebase/queries/groupMembershipGuard.ts — keep the
 * two in sync if this rule ever changes.
 */

export interface GuardGroup {
  id: string;
  groupType?: string;
  organization?: string;
}

export interface ExistingMembership {
  groupId: string;
  group: GuardGroup;
}

export type GroupMembershipGuardResult =
  | { ok: true }
  | { ok: false; reason: 'section-not-joinable' }
  | { ok: false; reason: 'subgroup-conflict'; existingGroupId: string };

export class SectionNotJoinableError extends Error {
  public readonly groupId: string;
  constructor(groupId: string) {
    super(`Group ${groupId} is a section (conceptual) and cannot be joined directly.`);
    this.name = 'SectionNotJoinableError';
    this.groupId = groupId;
  }
}

export class SubgroupConflictError extends Error {
  public readonly existingGroupId: string;
  public readonly targetGroupId: string;
  constructor(existingGroupId: string, targetGroupId: string) {
    super(
      `User already belongs to subgroup ${existingGroupId} in this organization; cannot also join ${targetGroupId}.`,
    );
    this.name = 'SubgroupConflictError';
    this.existingGroupId = existingGroupId;
    this.targetGroupId = targetGroupId;
  }
}

/**
 * Pure decision: may a user whose other memberships are `existingMemberships`
 * (every row EXCEPT the target group's own, if any) gain a membership row in
 * `targetGroup`?
 */
export function checkGroupMembershipGuard(
  targetGroup: GuardGroup,
  existingMemberships: ExistingMembership[],
): GroupMembershipGuardResult {
  if (targetGroup.groupType === 'parent') {
    return { ok: false, reason: 'section-not-joinable' };
  }
  if (!targetGroup.organization) {
    return { ok: true };
  }
  const conflict = existingMemberships.find(
    (m) =>
      m.groupId !== targetGroup.id &&
      m.group.groupType !== 'parent' &&
      m.group.organization === targetGroup.organization,
  );
  if (conflict) {
    return { ok: false, reason: 'subgroup-conflict', existingGroupId: conflict.groupId };
  }
  return { ok: true };
}
